import { useCallback, useId, useRef, type CSSProperties, type ReactNode } from 'react';

/**
 * The one implementation of hover disclosure in the product (FR-062, R-14).
 *
 * Every place information hides behind a hover — the full column label behind a short code
 * (FR-052), the full region name behind a region code (FR-053), the figures behind a trend
 * arrow (FR-054) — routes through here. That is deliberate: FR-062 applies to thousands of
 * cells, and exactly zero of them should carry bespoke markup.
 *
 * Three properties, all required for parity rather than a lesser second path:
 *
 * 1. **Focusable.** The trigger is a real `<button>`, so `Tab` reaches it. A `title` attribute
 *    or a visually-hidden `<span>` would satisfy a screen reader and leave keyboard users with
 *    no way to *reveal* anything.
 * 2. **Same content on `:hover` and `:focus-visible`** (see `theme/global.css`) — not a
 *    reduced version for one of them.
 * 3. **Exposed as an accessible name**, with any extra detail wired through
 *    `aria-describedby`. The visible short code is `aria-hidden`, so assistive technology
 *    announces "Recruiters — Nursing", not "REC slash N".
 *
 * The blueprint specifies the hover presentation and is silent on focus and assistive
 * technology, so all of this is additive: Principle I is untouched.
 *
 * ## Why the bubble lives in the top layer
 *
 * An absolutely-positioned panel is clipped by any ancestor that scrolls or hides overflow, and
 * `<DataTable>` has both — `overflow: hidden` on the card and `overflow-x: auto` on the scroller.
 * Inside the table that cropped the bubble to a sliver.
 *
 * `position: fixed` alone does not fix it either: `.rcard:hover` applies a `translateY`, and a
 * transformed ancestor becomes the containing block for fixed descendants, so the Cards view
 * would break instead. The blueprint sidesteps both by rendering one bubble at the document root.
 *
 * The **popover top layer** gets the same escape without a second element to keep in sync: a
 * showing popover is painted above everything and is affected by neither overflow nor ancestor
 * transforms. Coordinates are written straight to the node on hover rather than held in state —
 * a table renders thousands of these, and a re-render per pointer move is not worth it.
 *
 * The trade is that a closed popover is `display: none`, which hides it from assistive
 * technology. So the panel text is ALSO rendered as an always-present `.sr-only` node, and that
 * is what `aria-describedby` points at. The bubble itself is `aria-hidden`: screen readers get
 * the content unconditionally, sighted users get it positioned correctly, and neither depends
 * on the other.
 */

/** Gap between the trigger and the bubble, and the smallest margin kept against the viewport. */
const GAP = 9;
const EDGE = 8;

export interface PanelPlacement {
  left: number;
  top: number;
  /** `true` when the bubble sits **under** the trigger because it would not fit above. */
  below: boolean;
}

/**
 * Where the bubble goes — **pure, so it can be tested at all** (2026-09-13).
 *
 * ⚠ It is extracted for the reason `soleRegion` is: jsdom implements no popover API, so `show()`
 * returns before it ever positions anything and **no test in this product can reach the real
 * placement code**. A bug here is invisible to the suite by construction, which is exactly what
 * happened to the width rule this accompanies (`min-width: max-content` silently beat
 * `max-width`, and every long panel hung off the screen for as long as nobody hovered one).
 *
 * Two clamps, and each one only started to matter when panels grew past a single line:
 *
 * - **Horizontally** the bubble is centred on the trigger and then pushed back inside the
 *   viewport. ⚠ The bound is the bubble's **measured half-width**, not the 150px literal this
 *   replaced — that number was the blueprint's, chosen when every panel was a short label, and it
 *   is only correct while nothing exceeds 300px. A panel wider than the viewport is centred
 *   rather than clamped to an impossible range, which keeps both edges equally cut instead of
 *   dropping one off-screen entirely.
 * - **Vertically** it prefers to sit above, and flips below when there is not room. A one-line
 *   bubble never needed this; a wrapped four-line one near the top of a scrolled table does, and
 *   without it the panel that explains *why* a control is unusable is the one that gets cut off.
 */
export function panelPosition(
  trigger: { left: number; top: number; bottom: number; width: number },
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
): PanelPlacement {
  const half = panel.width / 2;
  const center = trigger.left + trigger.width / 2;

  const min = half + EDGE;
  const max = viewport.width - half - EDGE;
  // ⚠ `min > max` means the bubble is wider than the viewport can hold with margins — a real
  // case at 320px with a 280px panel. Clamping into an inverted range would pin it to `max` and
  // hang the left edge off-screen; centring cuts both sides evenly instead.
  const left = min > max ? viewport.width / 2 : Math.min(Math.max(center, min), max);

  const below = trigger.top - GAP - panel.height < EDGE;

  return { left, top: below ? trigger.bottom + GAP : trigger.top - GAP, below };
}

export interface DisclosureProps {
  /** What stays visible — the short code, the region code, the number. */
  children: ReactNode;
  /** The accessible name. Announced instead of the visible text. */
  accessibleName: string;
  /** Panel content. Defaults to `accessibleName`. */
  panel?: ReactNode;
  /** Extra detail announced via `aria-describedby` — e.g. both trend figures. */
  description?: string;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  title?: string;
  /**
   * Toggle state, for a trigger that is also a filter — the region chips.
   *
   * Omitted, the button announces as a plain button. Set, it announces as pressed or not, which
   * is the only thing telling a screen-reader user that the amber ring means "this filter is on".
   */
  pressed?: boolean;
  /**
   * Unavailable, but still able to explain itself.
   *
   * ⚠ **`aria-disabled`, never the `disabled` attribute.** A truly disabled button is removed
   * from the tab order and, in several engines, fires no pointer events at all — so the panel
   * that says *why* it is unavailable would be unreachable by exactly the reader who needs it.
   * That is the opposite of what this component is for (FR-062). The click is suppressed here
   * instead, which leaves the trigger focusable, hoverable and announced as disabled.
   */
  disabled?: boolean;
}

export function Disclosure({
  children,
  accessibleName,
  panel,
  description,
  onClick,
  className,
  style,
  pressed,
  disabled,
}: DisclosureProps) {
  const panelId = useId();
  const descriptionId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    // `showPopover` is absent on older engines. There the CSS fallback still reveals the
    // bubble on hover; it is merely clipped, which is what this replaced — never nothing.
    if (!trigger || !bubble || typeof bubble.showPopover !== 'function') return;

    /*
     * ⚠ **Shown BEFORE it is measured, which is the opposite of the old order.** A closed popover
     * is `display: none` and has no box at all, so its size cannot be read until it is open — and
     * the size is what both clamps now depend on. Writing the coordinates afterwards is safe
     * because this whole callback is one synchronous task: the browser lays out and paints once,
     * at the end, so there is no frame in which the bubble is visible at the previous position.
     */
    try {
      bubble.showPopover();
    } catch {
      // Already open — a focus following a hover on the same trigger.
    }

    // One forced layout per hover, not per pointer move — only one bubble is ever open. The
    // panel's size does not depend on the coordinates below (`max-width` fixes it), so reading
    // it before they are written is correct rather than merely convenient.
    const { width, height } = bubble.getBoundingClientRect();
    const { left, top, below } = panelPosition(
      trigger.getBoundingClientRect(),
      { width, height },
      { width: window.innerWidth, height: window.innerHeight },
    );

    bubble.style.left = `${left}px`;
    bubble.style.top = `${top}px`;
    // The class carries both the flipped `transform` and the arrow's side; see `global.css`.
    bubble.classList.toggle('disclosure-panel--below', below);
  }, []);

  const hide = useCallback(() => {
    const bubble = bubbleRef.current;
    if (!bubble || typeof bubble.hidePopover !== 'function') return;
    try {
      bubble.hidePopover();
    } catch {
      // Already closed.
    }
  }, []);

  /**
   * The bubble body is the panel alone.
   *
   * `description` — the 30- and 90-day figures — is deliberately NOT drawn in it (owner request
   * 2026-09-02). It sat as a trailing line under every breakdown, a second pair of numbers on
   * two windows the panel above them is not cut on, and it read as part of the decomposition.
   *
   * It is **not** removed from the component: the always-present `.sr-only` node below still
   * carries it and `aria-describedby` still points at it, so FR-054's "both figures stay
   * reachable" holds unchanged. Folding `description` back in here is what would break it.
   */
  const panelContent = panel ?? accessibleName;

  return (
    <button
      ref={triggerRef}
      type="button"
      className={className ? `disclosure ${className}` : 'disclosure'}
      style={style}
      // The accessible NAME. The visible content below is aria-hidden, so this is what is
      // announced — the full label, not the short code.
      aria-label={accessibleName}
      aria-describedby={description ? `${panelId} ${descriptionId}` : panelId}
      aria-pressed={pressed}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {children}
      </span>

      {/* The visual bubble. Top layer, so no ancestor can clip or reposition it. */}
      <span
        ref={bubbleRef}
        className="disclosure-panel"
        aria-hidden="true"
        {...{ popover: 'manual' }}
      >
        {panelContent}
      </span>

      {/* The same content for assistive technology, always present and never display:none. */}
      <span className="sr-only" id={panelId} role="tooltip">
        {panelContent}
      </span>

      {description ? (
        <span className="sr-only" id={descriptionId}>
          {description}
        </span>
      ) : null}
    </button>
  );
}
